import { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Button,
    Stack,
    CircularProgress,
    Alert,
    Tooltip,
    Chip,
    IconButton,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import RouteMap from "./RouteMap";
import CarrierLogo from "./CarrierLogo";
import { useSnackbar } from "notistack";
import i18n from "../locales/i18n";
import { getLineStationsByLineId, getTripStationNotes } from "../api/trips";

function CountryFlag({ alpha2, size = 16 }) {
    const [hasError, setHasError] = useState(false);
    const code = alpha2?.toUpperCase();
    if (!code || hasError) {
        return <Icon icon="mdi:flag-outline" width={size} color="#C4CDD5" />;
    }
    return (
        <Box
            component="img"
            src={`/assets/flags/${code}.svg`}
            alt={code}
            onError={() => setHasError(true)}
            sx={{ width: size, height: size * 0.75, objectFit: "contain", flexShrink: 0, borderRadius: 0.3 }}
        />
    );
}

function formatTime(dateStr) {
    if (!dateStr) return "--:--";
    try {
        if (typeof dateStr === "string" && dateStr.match(/^\d{2}:\d{2}/)) {
            return dateStr.substring(0, 5);
        }
        const date = new Date(dateStr);
        return date.toLocaleTimeString("sr-Latn", { hour: "2-digit", minute: "2-digit" });
    } catch {
        return dateStr;
    }
}

// Backend `lineStation` API vraća `departureTime`/`arrivalTime` kao proteklo
// (elapsed) vreme od početka linije, a ne stvarno vreme. Helperi ispod parsiraju
// HH:MM u minute i nazad, da bismo elapsed vrednosti mogli da vežemo za realno
// vreme polaska.
function parseTimeToMinutes(value) {
    if (value === null || value === undefined) return null;
    const str = formatTime(value);
    const match = str.match(/^(\d{1,2}):(\d{2})/);
    if (!match) return null;
    return parseInt(match[1], 10) * 60 + parseInt(match[2], 10);
}

function minutesToHHMM(total) {
    if (total === null || total === undefined) return null;
    const wrapped = ((total % (24 * 60)) + 24 * 60) % (24 * 60);
    const h = Math.floor(wrapped / 60);
    const m = wrapped % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function formatPrice(price) {
    if (!price && price !== 0) return "";
    return Number(price).toLocaleString("sr-Latn", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }) + " RSD";
}

function formatDuration(minutes) {
    if (!minutes && minutes !== 0) return null;
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function ResultHeader({ tripType }) {
    const { t } = useTranslation();
    const headers = [
        { label: t("results.departureTime"), align: "left" },
        { label: t("results.carrier"), align: "left" },
        { label: t("results.arrivalTime"), align: "left" },
        { label: t("results.freeSeats"), align: "center" },
        { label: tripType === "return" ? t("results.priceReturn") : t("results.price"), align: "right" },
        { label: "", align: "right" },
    ];
    return (
        <Box
            sx={{
                display: { xs: "none", md: "grid" },
                gridTemplateColumns: "140px 1fr 140px 120px 140px 120px",
                gap: 1,
                px: 2.5,
                py: 1.2,
                mb: 0.5,
            }}
        >
            {headers.map((col, i) => (
                <Typography
                    key={i}
                    variant="caption"
                    sx={{
                        fontWeight: 600,
                        color: "text.secondary",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        fontSize: "0.7rem",
                        textAlign: col.align,
                    }}
                >
                    {col.label}
                </Typography>
            ))}
        </Box>
    );
}

function TripDetailsPanel({ trip, departureStationObj, arrivalStationObj }) {
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const depStation = trip.departureStationName || trip.fromStation || "";
    const arrStation = trip.arrivalStationName || trip.toStation || "";
    const depStationId = trip.departureStationId || trip.fromStationId;
    const arrStationId = trip.arrivalStationId || trip.toStationId;
    const depTime = formatTime(trip.departureTime || trip.departureTimeStr);
    const arrTime = formatTime(trip.arrivalTime || trip.arrivalTimeStr);
    const lineId = trip.lineId;
    const duration = trip.timeTravelInMinutes ? formatDuration(trip.timeTravelInMinutes) : null;
    const platform = trip.platform || trip.peron || null;
    const returnTicket = trip.returnTicketAvailable ?? trip.hasReturnOption ?? null;
    const additionalDesc = trip.description || trip.routeDescription || trip.additionalDescription || "";

    const [stations, setStations] = useState([]);
    const [stationsLoading, setStationsLoading] = useState(false);
    const [notesByStation, setNotesByStation] = useState({});
    const tripIdForNotes = trip.tripId ?? trip.id;

    // Napomene prevoznika po stanici; njihov pad ne sme da sakrije itinerer.
    useEffect(() => {
        if (!tripIdForNotes) return undefined;
        let cancelled = false;
        getTripStationNotes(tripIdForNotes)
            .then((notes) => {
                if (cancelled) return;
                const map = {};
                notes.forEach((n) => { map[n.stationId] = n.comment; });
                setNotesByStation(map);
            })
            .catch((error) => console.error("Greška pri učitavanju napomena stanica:", error));
        return () => { cancelled = true; };
    }, [tripIdForNotes]);

    useEffect(() => {
        if (!lineId) return;
        let cancelled = false;
        setStationsLoading(true);
        getLineStationsByLineId(lineId)
            .then((data) => {
                if (!cancelled) setStations(data?.content || data || []);
            })
            .catch((error) => {
                console.error("Greška pri učitavanju stanica linije:", error);
                if (!cancelled) enqueueSnackbar(i18n.t("common.errorLoadingStations"), { variant: "error" });
            })
            .finally(() => { if (!cancelled) setStationsLoading(false); });
        return () => { cancelled = true; };
    }, [lineId, enqueueSnackbar]);

    // Niz stanica ima `departureTime`/`arrivalTime` kao proteklo (elapsed) vreme od
    // početka linije. Da bismo dobili stvarno vreme, vezujemo se za realno vreme
    // polaska sa naše polazne stanice (`trip.departureTime`, npr. 11:11) i na njega
    // dodajemo razliku elapsed vremena svake stanice u odnosu na našu polaznu.
    const depStationElapsedRaw = (() => {
        const match = stations.find((s) => (s.stationId || s.station?.id) === depStationId);
        const base = match || stations[0];
        return base ? (base.departureTime || base.arrivalTime) : null;
    })();
    const anchorClockMin = parseTimeToMinutes(depTime);
    const depStationElapsedMin = parseTimeToMinutes(depStationElapsedRaw);

    return (
        <Box sx={{ px: 3, py: 2.5, bgcolor: "#f8f9fb", borderTop: "1px solid", borderColor: "grey.200" }}>
            {/* Info red - trajanje + povratna */}
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                {duration && (
                    <Stack direction="row" spacing={0.8} alignItems="center">
                        <Icon icon="mdi:clock-outline" width={16} color="#919EAB" />
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8rem" }}>
                            {t("results.duration")}:
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: "#1a1a2e" }}>
                            {duration}
                        </Typography>
                    </Stack>
                )}
                {returnTicket === true && (
                    <Chip
                        icon={<Icon icon="mdi:swap-horizontal" width={14} />}
                        label={t("results.returnTicket")}
                        size="small"
                        sx={{ fontWeight: 600, fontSize: "0.7rem", height: 22, bgcolor: "#1a1a2e", color: "white", "& .MuiChip-icon": { color: "white" } }}
                    />
                )}
            </Stack>

            {/* Itinerer sa svim stanicama */}
            {stationsLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                    <CircularProgress size={22} />
                </Box>
            ) : stations.length > 0 ? (
                <Box sx={{ mb: 2.5, pb: 1 }}>
                    <Stack direction="row" alignItems="flex-start" useFlexGap sx={{ flexWrap: "wrap", rowGap: 2 }}>
                        {stations.map((ls, index) => {
                            const isLast = index === stations.length - 1;
                            const sId = ls.stationId || ls.station?.id;
                            const sName = ls.stationName || ls.station?.name || "";
                            const isFirst = index === 0;
                            const lsDepTime = formatTime(ls.departureTime);
                            const lsArrTime = formatTime(ls.arrivalTime);
                            // Stvarno vreme = vreme polaska sa naše stanice + (elapsed ove
                            // stanice - elapsed naše polazne stanice).
                            const lsElapsed = isFirst ? ls.departureTime : (ls.arrivalTime || ls.departureTime);
                            const lsElapsedMin = parseTimeToMinutes(lsElapsed);
                            let displayTime;
                            if (anchorClockMin !== null && depStationElapsedMin !== null && lsElapsedMin !== null) {
                                displayTime = minutesToHHMM(anchorClockMin + (lsElapsedMin - depStationElapsedMin));
                            }
                            if (!displayTime) displayTime = isFirst ? lsDepTime : (lsArrTime || lsDepTime);
                            const isDep = sId === depStationId;
                            const isArr = sId === arrStationId;
                            const isSelected = isDep || isArr;
                            const stationNote = notesByStation[sId];

                            return (
                                <Stack key={ls.id || index} direction="row" alignItems="flex-start">
                                    {/* Stanica */}
                                    <Stack alignItems="center" sx={{ width: isSelected ? 90 : 70 }}>
                                        <Box
                                            sx={{
                                                width: isSelected ? 14 : 8,
                                                height: isSelected ? 14 : 8,
                                                borderRadius: "50%",
                                                bgcolor: isSelected ? "#1a1a2e" : "white",
                                                border: "2px solid",
                                                borderColor: isSelected ? "#1a1a2e" : "grey.400",
                                                mb: 0.5,
                                            }}
                                        />
                                        <Typography
                                            sx={{
                                                fontFamily: "monospace",
                                                fontWeight: 700,
                                                fontSize: isSelected ? "0.8rem" : "0.7rem",
                                                color: isSelected ? "#1a1a2e" : "text.secondary",
                                            }}
                                        >
                                            {displayTime || "--:--"}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: isSelected ? "0.7rem" : "0.6rem",
                                                fontWeight: isSelected ? 700 : 400,
                                                color: isSelected ? "#1a1a2e" : "text.secondary",
                                                textAlign: "center",
                                                lineHeight: 1.2,
                                                maxWidth: isSelected ? 88 : 68,
                                            }}
                                        >
                                            {sName}
                                        </Typography>
                                        {isDep && (
                                            <Chip label={t("results.departure")} size="small" sx={{ mt: 0.5, height: 16, fontSize: "0.55rem", fontWeight: 700, bgcolor: "#1a1a2e", color: "white" }} />
                                        )}
                                        {isArr && (
                                            <Chip label={t("results.arrival")} size="small" sx={{ mt: 0.5, height: 16, fontSize: "0.55rem", fontWeight: 700, bgcolor: "#FF5630", color: "white" }} />
                                        )}
                                        {stationNote && (
                                            <Tooltip title={stationNote} arrow>
                                                <Typography
                                                    sx={{ mt: 0.5, fontSize: "0.6rem", color: "warning.dark", textAlign: "center", lineHeight: 1.2, maxWidth: isSelected ? 88 : 68, cursor: "help" }}
                                                >
                                                    <Icon icon="mdi:information-outline" width={12} style={{ verticalAlign: "-2px", marginRight: 2 }} />
                                                    {stationNote}
                                                </Typography>
                                            </Tooltip>
                                        )}
                                    </Stack>

                                    {/* Linija izmedju */}
                                    {!isLast && (
                                        <Box sx={{ height: 2, width: 24, bgcolor: "grey.300", mt: isSelected ? "6px" : "3px", flexShrink: 0 }} />
                                    )}
                                </Stack>
                            );
                        })}
                    </Stack>
                </Box>
            ) : (
                /* Fallback - samo polazak/dolazak */
                <Stack direction="row" alignItems="center" sx={{ mb: 2.5 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box sx={{ width: 14, height: 14, borderRadius: "50%", bgcolor: "primary.main", flexShrink: 0 }} />
                        <Stack>
                            <Typography variant="h6" component="div" sx={{ fontWeight: 800, lineHeight: 1.1 }}>{depTime}</Typography>
                            <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.75rem" }}>{depStation}</Typography>
                        </Stack>
                    </Stack>
                    <Box sx={{ flex: 1, height: 2, bgcolor: "primary.main", mx: 2, minWidth: 40 }} />
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box sx={{ width: 14, height: 14, borderRadius: "50%", bgcolor: "primary.main", flexShrink: 0 }} />
                        <Stack>
                            <Typography variant="h6" component="div" sx={{ fontWeight: 800, lineHeight: 1.1 }}>{arrTime}</Typography>
                            <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.75rem" }}>{arrStation}</Typography>
                        </Stack>
                    </Stack>
                </Stack>
            )}

            {/* Dodatne informacije */}
            <Stack direction={{ xs: "column", md: "row" }} spacing={{ xs: 1.5, md: 4 }} sx={{ mb: 2.5 }}>
                {platform && (
                    <Stack direction="row" spacing={1} alignItems="baseline">
                        <Typography variant="caption" sx={{ color: "primary.main", fontWeight: 700, fontSize: "0.75rem" }}>{t("booking.platform")}:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 800, color: "#1a1a2e" }}>{platform}</Typography>
                    </Stack>
                )}
                {additionalDesc && (
                    <Stack direction="row" spacing={1} alignItems="baseline">
                        <Typography variant="caption" sx={{ color: "primary.main", fontWeight: 700, fontSize: "0.75rem" }}>{t("results.description")}:</Typography>
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>{additionalDesc}</Typography>
                    </Stack>
                )}
            </Stack>

            {/* Mapa */}
            <RouteMap
                departureStation={departureStationObj || { name: depStation }}
                arrivalStation={arrivalStationObj || { name: arrStation }}
                depTime={depTime}
                arrTime={arrTime}
            />
        </Box>
    );
}

function TripRow({ trip, onSelectTrip, isExpanded, onToggleDetails, departureStationObj, arrivalStationObj, tripType }) {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const depStation = trip.departureStationName || trip.fromStation || "";
    const arrStation = trip.arrivalStationName || trip.toStation || "";
    const depLocation = trip.fromLocationName || trip.departureLocationName || trip.depLocationName || "";
    const arrLocation = trip.toLocationName || trip.arrivalLocationName || trip.arrLocationName || "";
    const depCountry = trip.departureCountryAlpha2 ||  "";
    const arrCountry = trip.arrivalCountryAlpha2 || "";
    const depTime = formatTime(trip.departureTime || trip.departureTimeStr);
    const arrTime = formatTime(trip.arrivalTime || trip.arrivalTimeStr);
    const carrier = trip.operatorName || trip.carrierName || "";
    const operatorId = trip.operatorId || trip.carrierId || null;
    const lineName = trip.lineName || trip.line?.name || "";
    const seats = trip.availableSeats ?? trip.freeSeats ?? "";
    const price = tripType === "return" && trip.returnPrice != null
        ? trip.returnPrice
        : (trip.price ?? trip.basePrice ?? null);

    if (isMobile) {
        return (
            <Box sx={{ mb: 1.5 }}>
                <Box sx={{ bgcolor: "white", p: 2, borderRadius: 1, border: "1px solid", borderColor: "grey.200", boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1.5 }}>
                        <Stack>
                            {depLocation && (
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                    <CountryFlag alpha2={depCountry} size={12} />
                                    <Typography sx={{ color: "text.secondary", fontSize: "0.55rem" }}>{depLocation}</Typography>
                                </Stack>
                            )}
                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", fontSize: "0.65rem" }}>
                                {depStation}
                            </Typography>
                            <Typography variant="h5" component="div" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
                                {depTime}
                            </Typography>
                        </Stack>
                        <Stack alignItems="center" sx={{ px: 2, pt: 0.5 }}>
                            <Icon icon="mdi:arrow-right" width={20} color="#919EAB" />
                        </Stack>
                        <Stack alignItems="flex-end">
                            {arrLocation && (
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                    <CountryFlag alpha2={arrCountry} size={12} />
                                    <Typography sx={{ color: "text.secondary", fontSize: "0.55rem" }}>{arrLocation}</Typography>
                                </Stack>
                            )}
                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", fontSize: "0.65rem" }}>
                                {arrStation}
                            </Typography>
                            <Typography variant="h5" component="div" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
                                {arrTime}
                            </Typography>
                        </Stack>
                    </Stack>

                    {carrier && (
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                            <CarrierLogo operatorId={operatorId} size={44} />
                            <Stack spacing={0}>
                                <Typography variant="body2" sx={{ color: "primary.main", fontWeight: 700, textTransform: "uppercase", lineHeight: 1.2 }}>
                                    {carrier}
                                </Typography>
                                {lineName && (
                                    <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem", lineHeight: 1.2 }}>
                                        {lineName}
                                    </Typography>
                                )}
                            </Stack>
                        </Stack>
                    )}

                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                        {seats !== "" && (
                            <Stack alignItems="center" spacing={0.3}>
                                <Typography variant="caption" color="text.secondary">{t("results.seats")}</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>{seats}</Typography>
                            </Stack>
                        )}
                        {price !== null && (
                            <Stack alignItems="flex-end" spacing={0.3}>
                                <Typography variant="caption" color="text.secondary">{t("results.price")}</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 800 }}>{formatPrice(price)}</Typography>
                            </Stack>
                        )}
                    </Stack>

                    <Stack direction="row" spacing={1}>
                        <Button
                            variant="outlined"
                            size="small"
                            fullWidth
                            onClick={onToggleDetails}
                            startIcon={<Icon icon={isExpanded ? "mdi:chevron-up" : "mdi:information-outline"} width={18} />}
                            sx={{ borderRadius: 1.5, fontWeight: 600, fontSize: "0.8rem" }}
                        >
                            {t("results.details")}
                        </Button>
                        <Button
                            variant="contained"
                            size="small"
                            fullWidth
                            onClick={() => onSelectTrip(trip)}
                            sx={{
                                borderRadius: 1.5,
                                fontWeight: 700,
                                fontSize: "0.8rem",
                                bgcolor: "#1a1a2e",
                                "&:hover": { bgcolor: "#16213e" },
                            }}
                        >
                            {t("common.buy")}
                        </Button>
                    </Stack>
                </Box>
                {isExpanded && (
                    <TripDetailsPanel trip={trip} departureStationObj={departureStationObj} arrivalStationObj={arrivalStationObj} />
                )}
            </Box>
        );
    }

    return (
        <Box sx={{ mb: 1.5 }}>
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: "140px 1fr 140px 120px 140px 120px",
                    gap: 1,
                    alignItems: "center",
                    px: 2.5,
                    py: 2,
                    bgcolor: "white",
                    borderRadius: isExpanded ? "12px 12px 0 0" : 2,
                    boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
                    border: "1px solid",
                    borderColor: isExpanded ? "grey.300" : "grey.200",
                    transition: "all 0.15s",
                    "&:hover": {
                        borderColor: "grey.400",
                        boxShadow: "0 3px 12px rgba(0,0,0,0.08)",
                    },
                }}
            >
                {/* Polazak */}
                <Box>
                    {depLocation && (
                        <Stack direction="row" spacing={0.5} alignItems="center">
                            <CountryFlag alpha2={depCountry} size={14} />
                            <Typography sx={{ color: "text.secondary", fontSize: "0.6rem", fontWeight: 500 }}>
                                {depLocation}
                            </Typography>
                        </Stack>
                    )}
                    <Typography variant="caption" sx={{ color: "text.secondary", textTransform: "uppercase", fontSize: "0.65rem", fontWeight: 500 }}>
                        {depStation}
                    </Typography>
                    <Typography variant="h5" component="div" sx={{ fontWeight: 800, lineHeight: 1.1, mt: 0.2 }}>
                        {depTime}
                    </Typography>
                </Box>

                {/* Prevoznik */}
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <CarrierLogo operatorId={operatorId} size={48} />
                    <Stack spacing={0}>
                        <Typography variant="body2" sx={{ color: "primary.main", fontWeight: 700, fontSize: "0.85rem", textTransform: "uppercase", lineHeight: 1.2 }}>
                            {carrier || "\u2014"}
                        </Typography>
                        {lineName && (
                            <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem", lineHeight: 1.2 }}>
                                {lineName}
                            </Typography>
                        )}
                    </Stack>
                </Stack>

                {/* Dolazak */}
                <Box>
                    {arrLocation && (
                        <Stack direction="row" spacing={0.5} alignItems="center">
                            <CountryFlag alpha2={arrCountry} size={14} />
                            <Typography sx={{ color: "text.secondary", fontSize: "0.6rem", fontWeight: 500 }}>
                                {arrLocation}
                            </Typography>
                        </Stack>
                    )}
                    <Typography variant="caption" sx={{ color: "text.secondary", textTransform: "uppercase", fontSize: "0.65rem", fontWeight: 500 }}>
                        {arrStation}
                    </Typography>
                    <Typography variant="h5" component="div" sx={{ fontWeight: 800, lineHeight: 1.1, mt: 0.2 }}>
                        {arrTime}
                    </Typography>
                </Box>

                {/* Sedista */}
                <Box sx={{ textAlign: "center" }}>
                    <Typography variant="h6" component="div" sx={{ fontWeight: 700 }}>
                        {seats !== "" ? seats : "\u2014"}
                    </Typography>
                </Box>

                {/* Cena */}
                <Box sx={{ textAlign: "right" }}>
                    <Typography variant="body1" sx={{ fontWeight: 800, fontSize: "0.95rem" }}>
                        {price !== null ? formatPrice(price) : "\u2014"}
                    </Typography>
                </Box>

                {/* Akcije - Kupi levo, Info desno */}
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button
                        variant="contained"
                        size="small"
                        onClick={() => onSelectTrip(trip)}
                        sx={{
                            bgcolor: "primary.main",
                            color: "white",
                            borderRadius: 1,
                            height: 36,
                            px: 2,
                            fontWeight: 700,
                            fontSize: "0.8rem",
                            textTransform: "none",
                            transition: "all 0.2s",
                            "&:hover": {
                                bgcolor: "primary.dark",
                            },
                        }}
                    >
                        {t("common.buy")}
                    </Button>
                    <Tooltip title={t("results.tripDetailsTooltip")}>
                        <IconButton
                            onClick={onToggleDetails}
                            size="small"
                            sx={{
                                border: "1px solid",
                                borderColor: isExpanded ? "grey.500" : "grey.300",
                                borderRadius: 1,
                                color: isExpanded ? "primary.main" : "text.secondary",
                                width: 36,
                                height: 36,
                                transition: "all 0.2s",
                                "&:hover": {
                                    borderColor: "grey.500",
                                    color: "primary.main",
                                    bgcolor: "grey.100",
                                },
                            }}
                        >
                            <Icon icon={isExpanded ? "mdi:chevron-up" : "mdi:information-outline"} width={20} />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Box>

            {/* Expandable Details */}
            {isExpanded && (
                <Box
                    sx={{
                        border: "1px solid",
                        borderColor: "grey.300",
                        borderTop: "none",
                        borderRadius: "0 0 12px 12px",
                        overflow: "hidden",
                    }}
                >
                    <TripDetailsPanel trip={trip} departureStationObj={departureStationObj} arrivalStationObj={arrivalStationObj} />
                </Box>
            )}
        </Box>
    );
}

export default function TripResultsList({ trips, isLoading, error, onSelectTrip, tripType = "oneWay", nextDate = null, onPickNextDate = null }) {
    const { t } = useTranslation();
    const [expandedId, setExpandedId] = useState(null);
    const { departureStation, arrivalStation } = useSelector((state) => state.search);

    const handleToggle = (tripId) => {
        setExpandedId((prev) => (prev === tripId ? null : tripId));
    };

    if (isLoading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                {error}
            </Alert>
        );
    }

    if (!trips || trips.length === 0) {
        // Relacija koja izabranog dana ne vozi nije ćorsokak — ako znamo prvi
        // sledeći dan sa polaskom, ponudimo skok na njega jednim klikom.
        const nextDateLabel = nextDate ? `${nextDate.slice(8, 10)}.${nextDate.slice(5, 7)}.${nextDate.slice(0, 4)}.` : null;
        return (
            <Alert
                severity="info"
                sx={{ mt: 2, borderRadius: 2, alignItems: "center" }}
                action={
                    nextDate && onPickNextDate ? (
                        <Button
                            color="inherit"
                            size="small"
                            variant="outlined"
                            startIcon={<Icon icon="mdi:calendar-arrow-right" width={16} />}
                            onClick={() => onPickNextDate(nextDate)}
                            sx={{ fontWeight: 700, whiteSpace: "nowrap", textTransform: "none" }}
                        >
                            {t("results.showNextDate", { date: nextDateLabel })}
                        </Button>
                    ) : undefined
                }
            >
                {nextDate
                    ? t("results.noResultsNextDate", { date: nextDateLabel })
                    : t("results.noResultsHint")}
            </Alert>
        );
    }

    return (
        <Box>
            <ResultHeader tripType={tripType} />
            {trips.map((trip, index) => {
                const tripKey = trip.id || trip.tripId || index;
                return (
                    <TripRow
                        key={tripKey}
                        trip={trip}
                        onSelectTrip={onSelectTrip}
                        isExpanded={expandedId === tripKey}
                        onToggleDetails={() => handleToggle(tripKey)}
                        departureStationObj={departureStation}
                        arrivalStationObj={arrivalStation}
                        tripType={tripType}
                    />
                );
            })}
        </Box>
    );
}
